/**
 * Admin accounts — creation, and the "this account is compromised" button.
 *
 * There is no list endpoint, deliberately on the API's side, so this screen
 * cannot enumerate admins. It does the two things the API allows: create one,
 * and revoke every session belonging to an id someone hands you.
 */

import { useState } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { Banner, ConfirmButton, ErrorBanner, Field } from '../components/ui.tsx';
import type { AdminRole, AdminView } from '../api/types.ts';

const ROLES: Array<{ id: AdminRole; blurb: string }> = [
  { id: 'finance', blurb: 'Reviews and decides deposits and withdrawals.' },
  { id: 'operator', blurb: 'Creates, starts and cancels rounds. Touches no money.' },
  { id: 'auditor', blurb: 'Reads the ledger, the audit trail and reconciliation. Changes nothing.' },
  { id: 'superadmin', blurb: 'Everything, including payment destinations and other admins.' },
];

export function Admins({ me }: { me: AdminView }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('finance');
  const [created, setCreated] = useState<{ admin: AdminView; otpauthUrl: string; totpSecret: string } | null>(
    null,
  );
  const [revokeId, setRevokeId] = useState('');
  const [revoked, setRevoked] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const mayCreate = can(me.role, 'admin.create');
  const mayRevoke = can(me.role, 'admin.revoke');

  async function create(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setCreated(await api.createAdmin({ username: username.trim(), password, role }));
      setUsername('');
      setPassword('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <header className="pagehead">
        <h1>Admins</h1>
      </header>

      <ErrorBanner error={error} />

      {created ? (
        <div className="card">
          <Banner kind="warn">
            <strong>Shown once.</strong> The API stores only a hash of this secret and there is no
            route that can read it back — a secret that can be re-read is one an attacker with a
            stolen session can also read. If this is lost, the account needs recreating.
          </Banner>
          <p>
            <strong>{created.admin.username}</strong> · {created.admin.role}
          </p>
          <p>
            Authenticator secret: <code className="hash">{created.totpSecret}</code>
          </p>
          <p className="muted">
            Enrolment URL — paste into an authenticator app:
            <br />
            <code className="hash">{created.otpauthUrl}</code>
          </p>
          <button type="button" className="button" onClick={() => setCreated(null)}>
            Done — hide this
          </button>
        </div>
      ) : null}

      {mayCreate ? (
        <div className="card">
          <h2>Create an admin</h2>
          <Field label="Username">
            <input
              className="input"
              autoCapitalize="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
          <Field label="Password" hint="At least 12 characters.">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Role">
            <select
              className="input"
              value={role}
              onChange={(e) => setRole(e.target.value as AdminRole)}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                </option>
              ))}
            </select>
          </Field>
          {/*
            The role blurb is shown rather than hidden behind documentation:
            §7.6 asks for scoped roles precisely so a deposit-approver is not a
            config-editor, and that only holds if whoever assigns the role knows
            what it grants at the moment they assign it.
          */}
          <p className="muted">{ROLES.find((r) => r.id === role)?.blurb}</p>
          <ConfirmButton
            className="button button--primary"
            confirm={`Create ${username.trim() || 'this admin'} as ${role}?`}
            disabled={busy || username.trim().length < 3 || password.length < 12}
            onConfirm={() => void create()}
          >
            Create admin
          </ConfirmButton>
        </div>
      ) : null}

      {mayRevoke ? (
        <div className="card">
          <h2>Revoke sessions</h2>
          <p className="muted">
            Signs an admin out everywhere, immediately. This is the button for a laptop left on a
            train or a password that may have been seen.
          </p>
          <div className="reasonform">
            <input
              className="input"
              placeholder="Admin id"
              value={revokeId}
              onChange={(e) => setRevokeId(e.target.value.trim())}
            />
            <ConfirmButton
              className="button button--danger"
              confirm="Sign this admin out of every session?"
              disabled={busy || !revokeId}
              onConfirm={() => {
                setBusy(true);
                setError(null);
                void api
                  .revokeSessions(revokeId)
                  .then(({ revoked: n }) => setRevoked(n))
                  .catch(setError)
                  .finally(() => setBusy(false));
              }}
            >
              Revoke
            </ConfirmButton>
          </div>
          {revoked !== null ? (
            <Banner kind="ok">
              {revoked} {revoked === 1 ? 'session' : 'sessions'} revoked.
            </Banner>
          ) : null}
        </div>
      ) : null}

      {!mayCreate && !mayRevoke ? (
        <p className="muted">Your role does not manage admin accounts.</p>
      ) : null}
    </section>
  );
}
